import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "./EmailApi";
import "./BackupEmailSection.css";

const defaultForm = {
  businessEmailId: "",
  backupName: "",
  backupType: "manual",
  backupSize: "250MB",
};

function BackupEmailSection() {
  const [emails, setEmails] = useState([]);
  const [backups, setBackups] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoringId, setRestoringId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedEmailExists = useMemo(() => {
    if (!form.businessEmailId) return false;
    return emails.some((item) => String(item.id) === String(form.businessEmailId));
  }, [emails, form.businessEmailId]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [emailsRes, backupsRes] = await Promise.all([
        apiGet("/api/email/my-emails"),
        apiGet("/api/email/backups"),
      ]);

      if (emailsRes?.success === false) {
        throw new Error(emailsRes?.message || "Failed to load business emails.");
      }

      if (backupsRes?.success === false) {
        throw new Error(backupsRes?.message || "Failed to load backups.");
      }

      setEmails(Array.isArray(emailsRes?.data) ? emailsRes.data : []);
      setBackups(Array.isArray(backupsRes?.data) ? backupsRes.data : []);
    } catch (err) {
      setError(err.message || "Backup data fetch failed.");
      setEmails([]);
      setBackups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    const intervalId = window.setInterval(fetchData, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchData]);

  const validateForm = () => {
    if (!form.businessEmailId) return "Please select a business email.";
    if (!selectedEmailExists) return "Selected business email is not valid.";
    if (!form.backupName.trim()) return "Backup name is required.";
    if (form.backupName.trim().length < 3) {
      return "Backup name must be at least 3 characters.";
    }
    if (!form.backupSize.trim()) return "Backup size is required.";

    return "";
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    setMessage("");
    setError("");

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setCreating(true);

      const data = await apiPost("/api/email/backups", {
        businessEmailId: form.businessEmailId,
        backupName: form.backupName.trim(),
        backupType: form.backupType,
        backupSize: form.backupSize.trim(),
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to create backup.");
      }

      setMessage(data.message || "Backup created successfully.");
      setForm(defaultForm);
      fetchData();
    } catch (err) {
      setError(err.message || "Failed to create backup.");
    } finally {
      setCreating(false);
    }
  };

  const handleRestore = async (id) => {
    if (!id) return;

    try {
      setRestoringId(String(id));
      setMessage("");
      setError("");

      const data = await apiPost(`/api/email/backups/${id}/restore`);

      if (!data?.success) {
        throw new Error(data?.message || "Failed to restore backup.");
      }

      setMessage(data.message || "Backup restored successfully.");
      fetchData();
    } catch (err) {
      setError(err.message || "Failed to restore backup.");
    } finally {
      setRestoringId("");
    }
  };

  return (
    <div className="email-panel-card br-section">
      <div className="br-header">
        <div>
          <span className="br-badge">Email Safety</span>
          <h2>Backup &amp; Recovery</h2>
          <p>
            Create backups, restore email data, and keep your business communication protected.
          </p>
        </div>

        <div className="br-icon-card">♻️</div>
      </div>

      {error ? <output className="email-error-box br-alert error">{error}</output> : null}
      {message ? <output className="email-success-box br-alert success">{message}</output> : null}

      <form className="email-panel-form br-form" onSubmit={handleCreate}>
        <select
          value={form.businessEmailId}
          onChange={(event) => updateForm("businessEmailId", event.target.value)}
          aria-label="Select business email"
          required
        >
          <option value="">Select Business Email</option>
          {emails.map((item) => (
            <option key={item.id} value={item.id}>
              {item.email_address || item.emailAddress || "Business Email"}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Backup Name"
          value={form.backupName}
          onChange={(event) => updateForm("backupName", event.target.value)}
          autoComplete="off"
          required
        />

        <select
          value={form.backupType}
          onChange={(event) => updateForm("backupType", event.target.value)}
          aria-label="Backup type"
        >
          <option value="manual">Manual</option>
          <option value="scheduled">Scheduled</option>
          <option value="recovery-point">Recovery Point</option>
        </select>

        <input
          type="text"
          placeholder="Backup Size"
          value={form.backupSize}
          onChange={(event) => updateForm("backupSize", event.target.value)}
          autoComplete="off"
          required
        />

        <button type="submit" disabled={creating || loading}>
          {creating ? "Creating..." : "Create Backup"}
        </button>
      </form>

      <div className="br-summary-row">
        <div>
          <strong>{backups.length}</strong>
          <span>Total Backups</span>
        </div>
        <div>
          <strong>{emails.length}</strong>
          <span>Business Emails</span>
        </div>
        <div>
          <strong>10s</strong>
          <span>Auto Refresh</span>
        </div>
      </div>

      <div className="email-panel-list br-list">
        {loading ? (
          <p className="br-empty">Loading backups...</p>
        ) : backups.length === 0 ? (
          <p className="br-empty">No backups found.</p>
        ) : (
          backups.map((item) => {
            const backupId = item.id;
            const isRestoring = restoringId === String(backupId);

            return (
              <div className="email-panel-item br-item" key={backupId}>
                <div className="br-item-top">
                  <div>
                    <h4>{item.backup_name || item.backupName || "Backup"}</h4>
                    <p>Email: {item.email_address || item.emailAddress || "-"}</p>
                  </div>

                  <span className="br-status">
                    {item.backup_status || item.backupStatus || "pending"}
                  </span>
                </div>

                <div className="br-meta-grid">
                  <p>Type: {item.backup_type || item.backupType || "-"}</p>
                  <p>Size: {item.backup_size || item.backupSize || "-"}</p>
                  <p>
                    Last Restored:{" "}
                    {item.last_restored_at || item.lastRestoredAt || "Not restored yet"}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => handleRestore(backupId)}
                  disabled={isRestoring}
                >
                  {isRestoring ? "Restoring..." : "Restore Backup"}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default BackupEmailSection;